import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import SelecteurProduction, {
  productionParDefaut,
} from '../../shared/components/SelecteurProduction';
import StateGuard from '../../shared/components/StateGuard';
import LimiteErreur from '../../shared/components/LimiteErreur';
import { useApiData } from '../../shared/hooks/useFetch';
import { api } from '../../shared/api/api';
import { RCP_OPTIONS, RCP_DEFAUT } from '../../shared/constants';
import EauChart from '../../shared/charts/EauChart';
import EauCumuleeChart from '../../shared/charts/EauCumuleeChart';
import EauCumuleeEcartChart from '../../shared/charts/EauCumuleeEcartChart';

/**
 * 2035-2050 / Eau et irrigation / Besoin en eau.
 *
 * Trois graphiques, tous alimentés par le besoin en eau (data.eau) : besoin en
 * eau journalier, besoin en eau cumulé, et besoin en eau cumulé en écart avec
 * 2020. L'irrigation (data.irrigation) n'est plus utilisée ici. En élevage,
 * seul le premier est affiché.
 */
export default function BesoinEnEau() {
  const { idExploit } = useOAD();
  const [production, setProduction] = React.useState(productionParDefaut);
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);

  const elevage = production.type === 'elevage';

  const parametres = React.useMemo(
    () => ({
      idExploitation: idExploit,
      type: production.type,
      idProduction: production.id,
      semis: production.semis,
      alimentation: production.alimentation,
      abreuvement: production.abreuvement,
      rcp,
    }),
    [idExploit, production, rcp]
  );

  const { data, loading, error } = useApiData(api.getEauIrrigation, parametres, {
    skip: !idExploit || !production.id,
  });

  const eauXMax = data?.eau?.length
    ? Math.max(...data.eau.map((d) => new Date(d.date_affichage).getTime()))
    : null;

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="eau-irrigation"
        niveau3="besoin-en-eau"
        idExploit={idExploit}
      >
        <SelecteurProduction valeur={production} onChange={setProduction} />

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-besoin-eau"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <LimiteErreur cle={`${production.id}-${rcp}`}>
        <StateGuard
          loading={loading}
          error={error}
          empty={!data || !data.eau?.length}
          emptyMessage="Pas de données de besoin en eau pour cette production."
        >
          {data && (
            <>
              <section className="oad-card">
                <h2 className="tdb-section-titre">
                  {elevage
                    ? 'Besoin en eau journalier des animaux'
                    : 'Besoin en eau journalier'}
                </h2>
                <EauChart data={data.eau} uniteElevage={elevage} />
              </section>

              {!elevage && (
                <>
                  <section className="oad-card">
                    <h2 className="tdb-section-titre">Besoin en eau cumulé</h2>
                    <EauCumuleeChart data={data.eau} xMax={eauXMax} />
                  </section>

                  <section className="oad-card">
                    <h2 className="tdb-section-titre">
                      Besoin en eau cumulé (écart à 2020)
                    </h2>
                    <EauCumuleeEcartChart data={data.eau} xMax={eauXMax} />
                  </section>
                </>
              )}
            </>
          )}
        </StateGuard>
        </LimiteErreur>
      </main>
    </div>
  );
}
